/**
 * useAdminAuth — контекст авторизации admin-панели.
 * Единственный хук. Использует adminAuth.ts (WebCrypto + sessionStorage).
 * БЕЗ edge functions, БЕЗ Supabase.
 */

import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import {
  adminLogin,
  adminLogout,
  isAdminAuthed,
  isAdminAuthedSync,
} from '../lib/adminAuth';

interface AdminAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Выполнить логин. Бросает Error при неверных данных. */
  login: (username: string, password: string) => Promise<void>;
  /** Выйти и сбросить сессию. */
  logout: () => void;
  /** Принудительно перепроверить сессию (async). */
  recheck: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(isAdminAuthedSync());
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const recheck = useCallback(async () => {
    const authed = await isAdminAuthed();
    setIsAuthenticated(authed);
  }, []);

  // Первичная async-проверка подписи
  useEffect(() => {
    let alive = true;
    isAdminAuthed().then((authed) => {
      if (alive) {
        setIsAuthenticated(authed);
        setIsLoading(false);
      }
    });
    return () => { alive = false; };
  }, []);

  const login = async (username: string, password: string): Promise<void> => {
    await adminLogin(username, password);
    setIsAuthenticated(true);
  };

  const logout = useCallback((): void => {
    adminLogout();
    setIsAuthenticated(false);
  }, []);

  return (
    <AdminAuthContext.Provider value={{ isAuthenticated, isLoading, login, logout, recheck }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth(): AdminAuthContextType {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
}
